import axios from 'axios';
import {mergeDeepRight} from 'ramda';

import {StaticConfig} from 'config/staticConfig';
import {AmicaApiRequest, ApiRequest} from 'shared/APIServices/types';
import {getApiUrl, getMockResponse, isEdgeAPI, isQuotingApi} from 'shared/APIServices/utils';
import {getQuotingStorage} from 'shared/Storage/utils/quoting';
import {getSessionUUID} from 'shared/utils/session';
import {getUserAgent} from 'utils/MobileBridge';
import {getValuesByKey} from 'utils/types';

export const buildPreloginAmicaApiRequest = async <Data extends {}, Params extends {}, Headers extends {}>(
  amicaApiRequest: AmicaApiRequest<Data, Params, Headers>
): Promise<any> => {
  const mockResponse = await getMockResponse(amicaApiRequest);
  if (mockResponse) {
    return mockResponse;
  }

  const sessionUUID = getSessionUUID();

  const decoratedRequest: ApiRequest = mergeDeepRight(amicaApiRequest || {}, {
    url: getApiUrl(amicaApiRequest.url) + amicaApiRequest.url,
    headers: {
      'Amica-Agent': !StaticConfig.mobileApplicationLoader ? 'Amica-Web' : getUserAgent(),
      'accept-language': 'en_US',
      'Amica-Method':
        amicaApiRequest.data && isEdgeAPI(amicaApiRequest.url)
          ? getValuesByKey(amicaApiRequest.data, 'method')
          : undefined,
      'x-gwre-session':
        (StaticConfig.edgeUUIDHeaderEnabled || StaticConfig.edgeUUIDHeaderAndCookieEnabled) &&
        isEdgeAPI(amicaApiRequest.url) &&
        sessionUUID
          ? sessionUUID
          : undefined,
      ...(buildQuoteHeaders(amicaApiRequest) || {}),
      ...(amicaApiRequest.headers || {})
    },
    withCredentials: StaticConfig.edgeUUIDHeaderEnabled ? false : isEdgeAPI(amicaApiRequest.url),
    apiAction: amicaApiRequest.apiAction?.join('/')
  });

  return axios.request(decoratedRequest);
};

const buildQuoteHeaders = <Data extends {method?: string; params?: any[]}, Params extends {}, Headers extends {}>(
  apiRequest: AmicaApiRequest<Data, Params, Headers>
) => {
  if (!isQuotingApi(apiRequest.url)) {
    return undefined;
  }
  const quotingStorage = getQuotingStorage();
  const isCreateSubmission = apiRequest.data?.method === 'createSubmission';

  const lobFromCreateSubmission = isCreateSubmission
    ? apiRequest.data?.params?.find((param: any) => param.policyType)?.policyType
    : undefined;

  const jobNumber = apiRequest.data?.params?.find((param: any) => param.jobNumber)?.jobNumber;
  const lobFromJobNumber = quotingStorage?.workOrders
    ? Object.entries(quotingStorage.workOrders).find((entry) => entry[1].jobNumber === jobNumber)?.[0]
    : undefined;

  const isEASQuote = quotingStorage?.workOrders
    ? Object.entries(quotingStorage.workOrders).some((entry) => entry[1].baseData.easOfferingsAvailable)
    : undefined;

  return {
    'Amica-Quote-State': quotingStorage?.state,
    'Amica-Quote-LOB': lobFromCreateSubmission ?? lobFromJobNumber,
    'Amica-Quote-EAS': !isCreateSubmission ? JSON.stringify(!!isEASQuote) : undefined
  };
};
